import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import type { PreparedStarterInfo, TagsCollection } from '@racemap/utilities/functions/timing';
import { isStageGroupEvent } from '@racemap/utilities/functions/utils';
import { defaultRangeExtractor, type Range, useVirtualizer } from '@tanstack/react-virtual';
import type { Immutable } from 'immer';
import { type CSSProperties, type FC, useCallback, useMemo, useRef, useState } from 'react';
import {
  useRelevantEventsData,
  useSelectedEventId,
} from 'src/store/leaderboard/leaderboard_reducer';
import { useWindowSize } from 'usehooks-ts';
import { PlayerLinkButton } from '../../BasicComponents/PlayerLinkButton/PlayerLinkButton';
import { SearchBar } from '../../BasicComponents/SearchBar/SearchBar';
import { getFilteredStarters, getSortedStarters, getTagsWithCounts } from '../../utils/helpers';
import { FilterModal } from '../FilterModal';
import { SettingsModal } from '../SettingsModal';
import { UploadButton } from '../UploadButton';
import { useLeaderboardEnvironmentInfo } from '../utils';
import { appendNoneTags, getFilteredTags } from './Columns/TagCol';
import {
  getColumnsForAtomicEvent,
  getColumnsForStageGroupEvent,
  getFixedColumnCount,
} from './helpers';
import { TableCell } from './TableCell';
import type { Sorting } from './TableHeaderCell';
import { TableHeaderCell } from './TableHeaderCell';

export interface Column {
  key: string;
  title: string | React.ReactNode;
  width: number;
  render: (row: Immutable<PreparedStarterInfo>) => React.ReactNode;
  headerRender?: (column: Column, sorting: Sorting | null) => React.ReactNode;
  sorter?: (a: Immutable<PreparedStarterInfo>, b: Immutable<PreparedStarterInfo>) => number;
}

export const TableBody: FC = () => {
  const { isEmbedded, isMobile } = useLeaderboardEnvironmentInfo();
  const selectedEventId = useSelectedEventId();
  const events = useRelevantEventsData();
  const selectedEventData = selectedEventId != null ? events.get(selectedEventId) : undefined;
  const isCompactVersion = isEmbedded || isMobile;

  if (selectedEventData == null) return null;

  const { eventObject, splits, tags, starterData, timekeepings } = selectedEventData;
  const filteredTags = getFilteredTags(tags, eventObject);
  const eventDataWithNoneTags = appendNoneTags(starterData, tags);

  const [showOnline, setShowOnline] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [showStartTime, setShowStartTime] = useState(false);
  const [showDeltaTime, setShowDeltaTime] = useState(false);
  const [selectedTags, setSelectedTags] = useState<TagsCollection>(new Map());
  const [sorting, setSorting] = useState<{ key: string; order: 'ASC' | 'DESC' } | null>(null);
  const { width: windowWidth } = useWindowSize();
  const containerRef = useRef<HTMLDivElement>(null);

  const starters = eventDataWithNoneTags.starters;
  const visibleElements = eventObject.modules.timing.visibleElements;
  const displayMapLink = visibleElements.mapLink && !isCompactVersion;
  const isStageGroup = isStageGroupEvent(eventObject);

  const columns: Array<Column> = [];
  if (isStageGroup) {
    columns.push(...getColumnsForStageGroupEvent(eventObject, filteredTags, events, windowWidth));
  } else {
    columns.push(
      ...getColumnsForAtomicEvent(
        eventObject,
        splits,
        eventDataWithNoneTags,
        timekeepings,
        filteredTags,
        windowWidth,
        showStartTime,
        showDeltaTime,
        isCompactVersion,
      ),
    );
  }

  const filteredStarters = useMemo(
    () => getFilteredStarters<PreparedStarterInfo>(starters, searchValue, showOnline, selectedTags),
    [starters, searchValue, showOnline, selectedTags],
  );

  const tagsWithCounts = useMemo(
    () => getTagsWithCounts(starters, filteredTags, selectedTags),
    [starters, filteredStarters, selectedTags],
  );
  const sortedStarters = useMemo(
    () => getSortedStarters(filteredStarters, sorting, columns, events, selectedEventData),
    [filteredStarters, sorting, columns, events, selectedEventData],
  );

  function cellRenderer({
    columnIndex,
    key,
    rowIndex,
    style,
  }: {
    columnIndex: number;
    key: string;
    rowIndex: number;
    style: CSSProperties;
  }) {
    const column = columns[columnIndex];
    if (rowIndex === 0) {
      return (
        <TableHeaderCell
          key={key}
          cellKey={key}
          style={style}
          column={column}
          sorting={sorting}
          setSorting={setSorting}
        />
      );
    }
    // Regular row
    const starter = sortedStarters[rowIndex - 1];

    return (
      <TableCell
        key={key}
        cellKey={key}
        style={style}
        column={column}
        starter={starter}
        rowIndex={rowIndex}
      />
    );
  }

  const fixedColumnCount = Math.min(getFixedColumnCount(eventObject, windowWidth), columns.length);
  const totalWidth = columns.reduce((sum, column) => sum + column.width, 0);

  const rowVirtualizer = useVirtualizer({
    count: sortedStarters.length + 1,
    getScrollElement: () => containerRef.current,
    estimateSize: (index) => (index === 0 ? 60 : 45),
    overscan: 20,
    // Always render the header row so it can stay pinned to the top.
    rangeExtractor: useCallback((range: Range) => {
      return [...new Set([0, ...defaultRangeExtractor(range)])].sort((a, b) => a - b);
    }, []),
  });

  return (
    <Container noPadding={isCompactVersion}>
      <ControlBar withPadding={isCompactVersion}>
        <SearchBar value={searchValue} onChange={setSearchValue} />
        {displayMapLink && <PlayerLinkButton event={eventObject} />}
        {filteredTags.size > 0 && (
          <FilterModal
            tags={tagsWithCounts}
            selectedTags={selectedTags}
            onChange={setSelectedTags}
          />
        )}
        <UploadButton />
        <SettingsModal
          event={eventObject}
          showStartTime={showStartTime}
          onShowStartTimeChange={setShowStartTime}
          showDeltaTime={showDeltaTime}
          onShowDeltaTimeChange={setShowDeltaTime}
          showOnline={showOnline}
          onShowOnlineChange={setShowOnline}
        />
      </ControlBar>
      <Content>
        <ScrollContainer ref={containerRef} noBorderRadius={isCompactVersion}>
          <div
            style={{
              position: 'relative',
              width: totalWidth,
              height: rowVirtualizer.getTotalSize(),
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const isHeaderRow = virtualRow.index === 0;

              const rowStyle: CSSProperties = isHeaderRow
                ? { position: 'sticky', top: 0, left: 0, zIndex: 2 }
                : {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transform: `translateY(${virtualRow.start}px)`,
                  };

              return (
                <div
                  key={virtualRow.index}
                  style={{
                    ...rowStyle,
                    width: totalWidth,
                    height: virtualRow.size,
                    display: 'flex',
                  }}
                >
                  {columns.map((column, columnIndex) => {
                    const isFixedColumn = columnIndex < fixedColumnCount;

                    const style: CSSProperties = {
                      position: isFixedColumn ? 'sticky' : 'static',
                      left: isFixedColumn
                        ? columns.slice(0, columnIndex).reduce((sum, c) => sum + c.width, 0)
                        : undefined,
                      flex: `0 0 ${column.width}px`,
                      width: column.width,
                      height: virtualRow.size,
                      zIndex: isFixedColumn ? 1 : 0,
                    };

                    return cellRenderer({
                      columnIndex,
                      rowIndex: virtualRow.index,
                      key: `${virtualRow.index}:${columnIndex}`,
                      style,
                    });
                  })}
                </div>
              );
            })}
          </div>
        </ScrollContainer>
      </Content>
    </Container>
  );
};

const Container = styled.div<{ noPadding: boolean }>`
  flex: 1;
  height: 100%;
  min-height: 0;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  padding: ${({ noPadding }) => (noPadding ? '0' : '10px 0')};
  margin: 0 auto;
    align-items: flex-start;
  width: fit-content;
  max-width: 100%;
`;

const ControlBar = styled.div<{ withPadding: boolean }>`
  max-width: 500px;
  display: flex;
  padding-bottom: 10px;
  gap: 5px;
  ${({ withPadding }) => (withPadding ? 'padding: 10px;' : '')}
`;

const Content = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  justify-content: flex-start;
  overflow: hidden;
`;

const ScrollContainer = styled.div<{ noBorderRadius: boolean }>`
  box-sizing: border-box;
  border: 1px solid ${RacemapColors.DarkGray};
  border-color: ${RacemapColors.LightGray};
  border-radius: ${({ noBorderRadius }) => (noBorderRadius ? '0' : '5px')};
  background-color: white;
  overflow: auto;
  max-width: 100%;

`;
